var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Stack, IconButton, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, Button, Hidden, Divider, } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RoasterOfferLists from '../RoasterOfferLists';
// FullCalendar
import FullCalendar from '@fullcalendar/react';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import googleCalendarPlugin from '@fullcalendar/google-calendar';
// FullCalendar locales
import jaLocale from '@fullcalendar/core/locales/ja';
import koLocale from '@fullcalendar/core/locales/ko';
import zhLocale from '@fullcalendar/core/locales/zh-tw';
import esLocale from '@fullcalendar/core/locales/es';
import itLocale from '@fullcalendar/core/locales/it';
import deLocale from '@fullcalendar/core/locales/de';
import frLocale from '@fullcalendar/core/locales/fr';
// sections
import { StyledCalendar, CalendarToolbar } from '../organisms/calendar';
// hooks
import useResponsive from '../../hooks/useResponsive';
// utils
import { fDate } from '../../utils/formatTime';
import i18n from '../../i18n';
import { convertLanguage } from '../../utils/languageUtils';
// ----------------------------------------------------------------------
var RoasterDashboard = function () {
    var t = useTranslation().t;
    var convertedLanguage = convertLanguage(i18n.language);
    return (React.createElement(Stack, { direction: "row", spacing: 6, sx: { width: '100%' } },
        React.createElement(Box, { sx: {
                display: {
                    xs: 'grid',
                    md: 'block',
                },
                padding: {
                    xs: '20px 16px',
                    md: '24px 0 24px 40px',
                },
            } },
            React.createElement(Accordion, { sx: { borderRadius: 1.5 } },
                React.createElement(AccordionSummary, { expandIcon: React.createElement(ExpandMoreIcon, null) },
                    React.createElement(Box, { sx: { typography: function (theme) { return theme.customTypography.subtitle1; } } }, t('schedule'))),
                React.createElement(AccordionDetails, null,
                    React.createElement(CustomFullCalendar, { viewValue: "dayGridMonth" }))),
            React.createElement(Box, { sx: {
                    mt: 5,
                    color: 'red',
                } },
                React.createElement(Trans, { i18nKey: "messages.newPlatformNotice" },
                    React.createElement("a", { href: "https://typica.coffee/".concat(convertedLanguage, "/offer-lists"), target: "_blank", rel: "noopener noreferrer", style: { color: 'red' } }))),
            React.createElement(Box, { component: "h4", sx: {
                    mt: 6,
                    mb: 3,
                    typography: function (theme) { return theme.customTypography.h4; },
                } }, t('offerLists')),
            React.createElement(RoasterOfferLists, null)),
        React.createElement(Hidden, { mdDown: true },
            React.createElement(Box, { sx: {
                    position: 'sticky',
                    top: 0,
                    minWidth: '400px',
                    height: '100vh',
                    overflow: 'auto',
                    boxShadow: '-1px 0 0 0 rgb(145 158 171 / 16%)',
                } },
                React.createElement(CustomFullCalendar, { viewValue: "listWeek" })))));
};
function CustomFullCalendar(_a) {
    var _b, _c;
    var viewValue = _a.viewValue;
    var calendarRef = React.useRef(null);
    var isDesktop = useResponsive('up', 'md');
    var _d = React.useState(isDesktop ? 'dayGridMonth' : 'listWeek'), view = _d[0], setView = _d[1];
    var _e = React.useState(new Date()), date = _e[0], setDate = _e[1];
    var _f = React.useState(null), selectedEvent = _f[0], setSelectedEvent = _f[1];
    var _g = React.useState(false), openForm = _g[0], setOpenForm = _g[1];
    var _h = useTranslation(), t = _h.t, i18n = _h.i18n;
    var props = {
        googleCalendarApiKey: 'AIzaSyA33H1LlycmRqRKL2c-iVVFMqxoa9Umu4Y',
        initialEvents: {
            googleCalendarId: 'c_r6bcnfvt8k7d32oimckv2q6i4k@group.calendar.google.com',
        },
        locales: [
            jaLocale,
            koLocale,
            zhLocale,
            esLocale,
            itLocale,
            deLocale,
            frLocale,
        ],
        locale: i18n.language,
    };
    React.useEffect(function () {
        var calendarEl = calendarRef.current;
        if (calendarEl) {
            var calendarApi = calendarEl.getApi();
            var newView = isDesktop ? viewValue : 'listWeek';
            calendarApi.changeView(newView);
            setView(newView);
        }
    }, [isDesktop, viewValue]);
    var handleClickToday = React.useCallback(function () {
        var calendarEl = calendarRef.current;
        if (calendarEl) {
            var calendarApi = calendarEl.getApi();
            calendarApi.today();
            setDate(calendarApi.getDate());
        }
    }, []);
    var handleClickDatePrev = React.useCallback(function () {
        var calendarEl = calendarRef.current;
        if (calendarEl) {
            var calendarApi = calendarEl.getApi();
            calendarApi.prev();
            setDate(calendarApi.getDate());
        }
    }, []);
    var handleClickDateNext = React.useCallback(function () {
        var calendarEl = calendarRef.current;
        if (calendarEl) {
            var calendarApi = calendarEl.getApi();
            calendarApi.next();
            setDate(calendarApi.getDate());
        }
    }, []);
    var handleOpenModal = React.useCallback(function () {
        setOpenForm(true);
    }, []);
    var handleCloseModal = React.useCallback(function () {
        setOpenForm(false);
        setSelectedEvent(null);
    }, []);
    var handleSelectEvent = React.useCallback(function (arg) {
        var _a, _b;
        arg.jsEvent.preventDefault();
        handleOpenModal();
        setSelectedEvent({
            id: arg.event.id,
            title: arg.event.title,
            url: arg.event.url,
            startStr: arg.event.startStr,
            endStr: arg.event.endStr,
            start: ((_a = arg.event.start) === null || _a === void 0 ? void 0 : _a.toISOString().split('.')[0]) + 'Z',
            end: ((_b = arg.event.end) === null || _b === void 0 ? void 0 : _b.toISOString().split('.')[0]) + 'Z',
        });
    }, [handleOpenModal]);
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledCalendar, null,
            React.createElement(CalendarToolbar, { date: date, view: viewValue, onNextDate: handleClickDateNext, onPrevDate: handleClickDatePrev, onToday: handleClickToday }),
            React.createElement(FullCalendar, __assign({ weekends: true, ref: calendarRef, rerenderDelay: 10, eventDisplay: "block", height: isDesktop ? 'auto' : 400, headerToolbar: false, initialView: view, eventClick: handleSelectEvent, plugins: [listPlugin, dayGridPlugin, googleCalendarPlugin] }, props))),
        React.createElement(Dialog, { fullWidth: true, maxWidth: "sm", open: openForm, onClose: handleCloseModal, sx: { mx: 2 } },
            React.createElement(DialogTitle, { sx: {
                    padding: '20px 56px 20px 24px',
                    typography: function (theme) { return theme.customTypography.h6; },
                } }, selectedEvent === null || selectedEvent === void 0 ? void 0 :
                selectedEvent.title,
                React.createElement(IconButton, { "aria-label": "close", onClick: handleCloseModal, sx: {
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: function (theme) { return theme.customPalette.grey[500]; },
                    } },
                    React.createElement(CloseIcon, null))),
            React.createElement(DialogContent, null, selectedEvent ? (React.createElement(Stack, { direction: "row", spacing: 1.5, sx: { typography: function (theme) { return theme.customTypography.body1; } } },
                React.createElement(Box, { sx: { fontWeight: 'bold' } },
                    t('when'),
                    ":"),
                React.createElement(Box, null,
                    fDate(selectedEvent.startStr, '', i18n.language),
                    " -",
                    ' ',
                    fDate(selectedEvent.endStr, '', i18n.language)))) : ('')),
            React.createElement(Divider, null),
            React.createElement(Button, { variant: "text", href: "https://www.google.com/calendar/render?action=TEMPLATE&dates=".concat((_b = selectedEvent === null || selectedEvent === void 0 ? void 0 : selectedEvent.start) === null || _b === void 0 ? void 0 : _b.split(/-|:/).join(''), "/").concat((_c = selectedEvent === null || selectedEvent === void 0 ? void 0 : selectedEvent.end) === null || _c === void 0 ? void 0 : _c.split(/-|:/).join(''), "&text=").concat(selectedEvent === null || selectedEvent === void 0 ? void 0 : selectedEvent.title), target: "_blank", rel: "noreferrer", sx: { py: 3 } }, t('add_to_google_calendar')))));
}
export default RoasterDashboard;
